import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import List from "../../../components/List/List"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { companyMemory, orderMemory } from "../../../Objects/Memory/Memory"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { ORDER_LIST_PAGE_SIZE, type OrderPaginationState, useGetOrder } from "../../../hooks/Order/useGetOrder"
import { useReloadMemory } from "../../../hooks/useReloadMemory"
import { useTranslation } from "react-i18next"
import { OrderPupop } from "./OrderPupop"
import { OrderPupopClose } from "./OrderPupopClose"
import { faCommentDollar, faFilter } from "@fortawesome/free-solid-svg-icons"
import { converteDateToSql } from "../../../Objects/Generics/Helps"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import THEME from "../../../configuration/Theme"
import { OrderAdvancedFiltersPopup } from "./OrderAdvancedFiltersPopup"
import { getStoreNow } from "../../../utils/timeZone"
import { ERequestType } from "../../../Enum/ERequestType"
import { useOnboardingCreatePopup } from "../../../hooks/useOnboardingCreatePopup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"
import { useSearchParams } from "react-router-dom"

export const OrderPage: React.FC = () => {
  const { t } = useTranslation("order")

  const currentDate = getStoreNow(companyMemory.timeZoneId).toInputDate()
  
  const [searchParams, setSearchParams] = useSearchParams()

  const [enableCommands, setEnableCommands] = useState(false)

  const { handleGetOrder } = useGetOrder()

  const handleGetOrderRef = useRef(handleGetOrder)

  const [isFlagLoading] = useState(false)

  const [orderData, setOrderData] = useState<OrderEntity | undefined>(undefined)

  const { handleReloadToMemory } = useReloadMemory()

  const handleReloadToMemoryRef = useRef(handleReloadToMemory)

  const dateStart = searchParams.get("dateStart")?.trim() || currentDate

  const dateEnd = searchParams.get("dateEnd")?.trim() || currentDate

  const isNegotiationMode = searchParams.get("negotiationMode") === "true"

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  const filterCustomerName = searchParams.get("customerName")?.trim() || ""

  const filterCustomerDocument = searchParams.get("customerDocument")?.trim() || ""

  const filterOrderCode = searchParams.get("orderCode")?.trim() || ""

  const filterOnlyOpenLostOrders = searchParams.get("onlyOpenLostOrders") === "true"

  const [paginationState, setPaginationState] = useState<OrderPaginationState>({
    pageNumber: 1,
    pageSize: ORDER_LIST_PAGE_SIZE,
    totalCount: 0,
    hasNextPage: false,
  })

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const isInitialFilterLoad = useRef(true)

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
    handleReloadToMemoryRef.current = handleReloadToMemory
  }, [handleGetOrder, handleReloadToMemory])

  const handleOpenEditePromotion = (id: number) => {
    const promotion = orderMemory.findById(id)!

    handleSwitchEditeOrAdd(true)

    setOrderData(promotion)
  }

  const handlerRefresh = useCallback(async (
    loadAll: boolean = true,
    filters: URLSearchParams = searchParams,
    pageNumber: number = 1,
    append: boolean = false,
  ) => {
    if (loadAll) {
      await handleReloadToMemoryRef.current()
    }

    const startDate = filters.get("dateStart")?.trim() || currentDate
    
    const endDate = filters.get("dateEnd")?.trim() || currentDate
    
    const negotiationMode = filters.get("negotiationMode") === "true"
    
    const onlyOpenLostOrders = filters.get("onlyOpenLostOrders") === "true"
    
    const route = negotiationMode ? API_ROUTES.ORDER.GET_ALL_NEGOTIATION : API_ROUTES.ORDER.GET_ALL

    const nextPaginationState = await handleGetOrderRef.current(
      () => {},
      startDate.length === 0 ? converteDateToSql(getStoreNow(companyMemory.timeZoneId)) : startDate,
      endDate.length === 0 ? converteDateToSql(getStoreNow(companyMemory.timeZoneId)) : endDate,
      route,
      ERequestType.Store,
      negotiationMode ? false : onlyOpenLostOrders,
      false,
      {
        pageNumber,
        pageSize: ORDER_LIST_PAGE_SIZE,
        append,
        customerName: filters.get("customerName")?.trim() || undefined,
        customerDocument: filters.get("customerDocument")?.trim() || undefined,
        orderCode: filters.get("orderCode")?.trim() || undefined,
      },
    )

    if (nextPaginationState) {
      setPaginationState(nextPaginationState)
    }
  }, [currentDate, searchParams])

  const handleLoadMoreOrders = useCallback(async () => {
    if (isLoadingMore || !paginationState.hasNextPage) {
      return
    }

    setIsLoadingMore(true)

    try {
      await handlerRefresh(false, searchParams, paginationState.pageNumber + 1, true)
    } finally {
      setIsLoadingMore(false)
    }
  }, [handlerRefresh, isLoadingMore, paginationState.hasNextPage, paginationState.pageNumber, searchParams])

  useEffect(() => {
    const load = async () => {
      const loadAll = isInitialFilterLoad.current

      isInitialFilterLoad.current = false

      await handlerRefresh(loadAll)
    }

    load()
  }, [
    dateStart,
    dateEnd,
    filterCustomerDocument,
    filterCustomerName,
    filterOnlyOpenLostOrders,
    filterOrderCode,
    handlerRefresh,
    isNegotiationMode,
  ])

  const handleToggleNegotiationMode = () => {
    const nextMode = !isNegotiationMode
    const nextSearchParams = new URLSearchParams(searchParams)

    if (nextMode) {
      nextSearchParams.set("negotiationMode", "true")
    } else {
      nextSearchParams.delete("negotiationMode")
    }

    setSearchParams(nextSearchParams, { replace: true })
  }

  const handleOpenAdvancedFilters = () => {
    setShowAdvancedFilters(true)
  }

  useQuickActionPopup("orders-advanced-filters", handleOpenAdvancedFilters)

  const handleCloseAdvancedFilters = () => {
    setShowAdvancedFilters(false)
  }

  const handleApplyAdvancedFilters = (filters: {
    dateStart: string
    dateEnd: string
    customerName: string
    customerDocument: string
    orderCode: string
    onlyOpenLostOrders: boolean
  }) => {
    const normalizedStart = filters.dateStart.length === 0 ? currentDate : filters.dateStart

    const normalizedEndBase = filters.dateEnd.length === 0 ? currentDate : filters.dateEnd

    const normalizedEnd = normalizedEndBase < normalizedStart ? normalizedStart : normalizedEndBase
    
    const nextSearchParams = new URLSearchParams(searchParams)

    nextSearchParams.set("dateStart", normalizedStart)
    
    nextSearchParams.set("dateEnd", normalizedEnd)

    const customerName = filters.customerName.trim()
    
    const customerDocument = filters.customerDocument.trim()
    
    const orderCode = filters.orderCode.trim()

    customerName.length > 0
      ? nextSearchParams.set("customerName", customerName)
      : nextSearchParams.delete("customerName")

    customerDocument.length > 0
      ? nextSearchParams.set("customerDocument", customerDocument)
      : nextSearchParams.delete("customerDocument")

    orderCode.length > 0 ? nextSearchParams.set("orderCode", orderCode) : nextSearchParams.delete("orderCode")

    filters.onlyOpenLostOrders
      ? nextSearchParams.set("onlyOpenLostOrders", "true")
      : nextSearchParams.delete("onlyOpenLostOrders")

    setSearchParams(nextSearchParams, { replace: true })
    
    setShowAdvancedFilters(false)
  }

  const filteredCards = orderMemory
    .getAll()
    .filter((order) => {
      const customerName = order.getCustomerDisplayName().toUpperCase()

      const customerDocument = `${order.getCustomerDisplayCpf()} ${order.customerEntity?.personEntity?.rg ?? ""}`
        .toUpperCase()
        .trim()
      const orderCode = order.id.toString()

      const customerNameMatch =
        filterCustomerName.trim().length === 0 || customerName.includes(filterCustomerName.trim().toUpperCase())

      const customerDocumentMatch =
        filterCustomerDocument.trim().length === 0 ||
        customerDocument.includes(filterCustomerDocument.trim().toUpperCase())

      const orderCodeMatch = filterOrderCode.trim().length === 0 || orderCode.includes(filterOrderCode.trim())

      return customerNameMatch && customerDocumentMatch && orderCodeMatch
    })
    .map((order, index) => orderMemory.getCard(order, index))

  const handleSwitchEditeOrAdd = (status: boolean) => {
    setOrderData(undefined)

    setEnableCommands(status)
  }

  useOnboardingCreatePopup(() => handleSwitchEditeOrAdd(true))

  return (
    <>
      {!isFlagLoading && (
        <>
          <div className={defaultStyle.containerList}>
            <List
              data={filteredCards}
              messageCount={t("list:label_order_page_message_count")}
              messageEmpty={t("list:label_order_page_message_empty")}
              nameFindSearch={t("list:label_order_page_place_holder")}
              handleAddButton={isNegotiationMode ? undefined : () => handleSwitchEditeOrAdd(true)}
              handleRefreshButton={() => handlerRefresh(false)}
              handleExtraFloatButton={handleOpenAdvancedFilters}
              extraFloatButtonDescriptionKey="interactionDescriptions:pages.fluxo.order.orderPage.button.openAdvancedFilters"
              extraFloatButtonIcon={faFilter}
              extraFloatButtonColor={THEME.colors.primary}
              handleSecondaryExtraFloatButton={handleToggleNegotiationMode}
              secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:pages.fluxo.order.orderPage.button.toggleNegotiationMode"
              secondaryExtraFloatButtonIcon={faCommentDollar}
              secondaryExtraFloatButtonColor={
                isNegotiationMode ? THEME.colors.secondary : THEME.colors.buttonBGSaveColor
              }
              showFilterStatus={false}
              hasMore={paginationState.hasNextPage}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMoreOrders}
              disableLocalOrdenation
              handleOnClick={(e) => handleOpenEditePromotion(e)}
            />
          </div>

          {enableCommands && ((orderData && !orderData.orderPaymentEntity.isClosed()) || !orderData) && (
            <OrderPupop
              order={orderData}
              title={orderData ? t("title_popup_edite") : t("title_popup_create")}
              onClonse={() => handleSwitchEditeOrAdd(false)}
              onSave={() => handlerRefresh(false)}
            />
          )}

          {enableCommands && orderData && orderData.orderPaymentEntity.isClosed() && (
            <OrderPupopClose
              order={orderData}
              onClonse={() => handleSwitchEditeOrAdd(false)}
              onSave={() => handlerRefresh(false)}
            />
          )}

          {showAdvancedFilters && (
            <OrderAdvancedFiltersPopup
              initialFilters={{
                dateStart,
                dateEnd,
                customerName: filterCustomerName,
                customerDocument: filterCustomerDocument,
                orderCode: filterOrderCode,
                onlyOpenLostOrders: filterOnlyOpenLostOrders,
              }}
              onApplyFilters={handleApplyAdvancedFilters}
              onClose={handleCloseAdvancedFilters}
            />
          )}
        </>
      )}
    </>
  )
}
